import { Injectable, inject, OnDestroy } from '@angular/core'
import { Subscription, catchError, forkJoin, interval, map, of, switchMap } from 'rxjs'
import { Doctor } from '../store/interfaces/doctor'
import { Operator } from '../store/interfaces/operator'
import { extractContractIdsFromUser } from '../permissions/data-scope.util'
import { ChannelsService } from './channels.service'
import { ContractsService } from './contracts.service'
import { SpecialtiesService } from './specialties.service'
import { UserService } from './user.service'

/** Refresca la caché de scope en servidor un poco antes del TTL (10 min en API). */
const SCOPE_WARM_INTERVAL_MS = 8 * 60 * 1000

@Injectable({ providedIn: 'root' })
export class SessionWarmService implements OnDestroy {
  private readonly userService = inject(UserService)
  private readonly channelsService = inject(ChannelsService)
  private readonly contractsService = inject(ContractsService)
  private readonly specialtiesService = inject(SpecialtiesService)
  private refreshSub: Subscription | null = null
  private sessionUser: Doctor | Operator | null = null

  warmStableReferenceData(user: Doctor | Operator, forceRefresh = false) {
    const contractIds = extractContractIdsFromUser(user)
    return forkJoin({
      channels: this.channelsService.getChannels(forceRefresh).pipe(catchError(() => of([]))),
      specialties: this.specialtiesService.getSpecialties(forceRefresh).pipe(catchError(() => of([]))),
      contracts: contractIds.length
        ? this.contractsService.getContractsByIds(contractIds).pipe(catchError(() => of(null)))
        : of<unknown[]>([]),
    }).pipe(map(() => undefined))
  }

  startPeriodicScopeWarm(user?: Doctor | Operator | null): void {
    this.stopPeriodicScopeWarm()
    this.sessionUser = user ?? null
    this.refreshSub = interval(SCOPE_WARM_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          forkJoin({
            scope: this.userService.warmSessionCache(),
            referenceData: this.sessionUser ? this.warmStableReferenceData(this.sessionUser, true) : of(undefined),
          }).pipe(catchError(() => of(null))),
        ),
      )
      .subscribe()
  }

  stopPeriodicScopeWarm(): void {
    this.refreshSub?.unsubscribe()
    this.refreshSub = null
    this.sessionUser = null
  }

  ngOnDestroy(): void {
    this.stopPeriodicScopeWarm()
  }
}
